'use client';

import * as React from 'react';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * Shortcut to the back office. Rendered from the (site) layout in local dev and on
 * demo sites (a demo behaves like dev); hidden once the site is paid (demo mode
 * off) so real visitors never see it. The admin secret/password still gates /admin.
 *
 * Instead of navigating away to `/admin`, it opens the whole back office in a
 * full-screen dialog (hosted in an iframe) so you stay on the page you're
 * previewing. The standalone `/admin` route still works on its own.
 */
export default function DevAdminButton() {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Tooltip title="Back office" placement="left">
        <Fab
          onClick={() => setOpen(true)}
          color="default"
          size="medium"
          aria-label="Ouvrir le back office"
          data-bridge-ignore="fab"
          // Above the page's own chrome (app bars, drawers, sticky headers) but
          // UNDER any dialog: floating over the demo intro — or over its own back
          // office — made it read as part of the dialog.
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (t) => t.zIndex.modal - 1 }}
        >
          <LockIcon />
        </Fab>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-label="Back office"
        // The view bridge serializer skips this subtree (the back office is not
        // part of "what the site shows") and uses it to tell the assistant
        // whether the owner currently has the back office open on top.
        data-bridge-ignore="admin"
        maxWidth={false}
        PaperProps={{
          sx: { width: '85vw', height: '85vh', maxWidth: '85vw', maxHeight: '85vh', m: 0 },
        }}
      >
        <AppBar position="relative" color="default" elevation={1}>
          <Toolbar variant="dense">
            <LockIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Back office
            </Typography>
            <Tooltip title="Ouvrir dans un nouvel onglet">
              <IconButton
                component="a"
                href="/admin"
                target="_blank"
                rel="noopener"
                aria-label="Ouvrir le back office dans un nouvel onglet"
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            <IconButton edge="end" onClick={() => setOpen(false)} aria-label="Fermer le back office">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {/* Only mount the iframe while open so the back office loads fresh each time. */}
          {open && (
            <Box
              component="iframe"
              src="/admin"
              title="Back office"
              sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
}
